"use client";

import { useHotkeys } from "@mantine/hooks";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import {
	CHANGELOG_HOTKEY_EDIT,
	CHANGELOG_HOTKEY_VIEW,
	CHANGELOG_PASSKEY,
	CHANGELOG_PASSKEY_STORAGE,
} from "~/data/changelog-config";
import useChangelogStore from "~/stores/changelog";
import { ChangelogModal } from "./changelog-modal";

const PasskeyDialog = ({
	open,
	onOpenChange,
	onUnlock,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onUnlock: (passkey: string) => void;
}) => {
	const [value, setValue] = useState("");
	const [invalid, setInvalid] = useState(false);

	return (
		<Dialog onOpenChange={onOpenChange} open={open}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<KeyRound className="h-4 w-4 text-primary" />
						Editor passkey
					</DialogTitle>
					<DialogDescription>
						Enter the passkey to unlock the changelog editor.
					</DialogDescription>
				</DialogHeader>
				<form
					className="flex flex-col gap-3"
					onSubmit={(event) => {
						event.preventDefault();
						if (value === CHANGELOG_PASSKEY) {
							onUnlock(value);
							setValue("");
							setInvalid(false);
						} else {
							setInvalid(true);
						}
					}}
				>
					<Input
						autoFocus
						onChange={(event) => {
							setValue(event.target.value);
							setInvalid(false);
						}}
						placeholder="Passkey"
						type="password"
						value={value}
					/>
					{invalid && (
						<p className="text-destructive text-xs">Incorrect passkey.</p>
					)}
					<Button disabled={value.length === 0} type="submit">
						Unlock editor
					</Button>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export const ChangelogRoot = () => {
	const openViewer = useChangelogStore((state) => state.openViewer);
	const openEditor = useChangelogStore((state) => state.openEditor);
	const [askForKey, setAskForKey] = useState(false);

	const requestEditor = () => {
		const remembered =
			typeof window === "undefined"
				? null
				: window.localStorage.getItem(CHANGELOG_PASSKEY_STORAGE);
		if (remembered === CHANGELOG_PASSKEY) {
			openEditor(remembered);
			return;
		}
		setAskForKey(true);
	};

	useHotkeys([
		[CHANGELOG_HOTKEY_VIEW, () => openViewer()],
		[CHANGELOG_HOTKEY_EDIT, () => requestEditor()],
	]);

	return (
		<>
			<ChangelogModal />
			<PasskeyDialog
				onOpenChange={setAskForKey}
				onUnlock={(passkey) => {
					window.localStorage.setItem(CHANGELOG_PASSKEY_STORAGE, passkey);
					setAskForKey(false);
					openEditor(passkey);
				}}
				open={askForKey}
			/>
		</>
	);
};
